import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';

import { OddsAcceptanceMode } from '@bpos';
import { EpcotConfig, OddsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../base/services/betslip-messages-provider.service';
import { IBetslipDigitalRootState } from '../../../digital/state';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import { betslipPicksListSelector } from '../../picks/selectors';
import LinearBetslipTrackingActions from '../../tracking/state/linear-tracking.actions';
import SettingsActions from '../actions';
import DigitalSettingsActions from '../digital-actions';
import { settingsSelector } from '../selectors';
import NotificationService, { INotificationStatus } from '../services/notification.service';

export enum NotifyType {
    App = 'app',
    Email = 'email',
}

@Component({
    selector: 'bs-digital-settings',
    templateUrl: './digital-settings.component.html',
    styleUrls: ['./digital-settings.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export default class DigitalSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() settingsOpened = new EventEmitter<number>();
    @ViewChild('settingsContent', { static: true }) settingsContent: ElementRef;

    readonly showDoneButton: boolean;

    readonly acceptance = {
        Any: OddsAcceptanceMode.Any,
        Higher: OddsAcceptanceMode.Higher,
        No: OddsAcceptanceMode.No,
    };

    readonly notifyTypes = NotifyType;
    readonly messages: {
        title: string;
        acceptAnyOdds: string;
        acceptAnyExperience: string;
        acceptHigher: string;
        acceptNoChanges: string;
        acceptTac: string;
        appNotification: string;
        emailNotification: string;
    };

    readonly linearMessages: {
        settingsAcceptanceTitle: string;
        settingsNotificationsTitle: string;
        settingsDone: string;
    };

    readonly globalNotificationStatus: INotificationStatus;
    selectedOddAcceptance: OddsAcceptanceMode = OddsAcceptanceMode.No;
    notificationsViewModel: { [key: string]: { label: string; visible: boolean; enabled: boolean; value: boolean } };

    readonly picksChangeSub: Subscription;
    readonly settingsChangeSub: Subscription;
    hideAppNotificationCheckBox = false;
    hideNotificationsSection = false;

    constructor(
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: ILinearBetslipMessagesProviderService,
        private store: Store<IBetslipDigitalRootState>,
        notificationService: NotificationService,
        changeDetector: ChangeDetectorRef,
        private deviceService: DeviceService,
        public oddsConfig: OddsConfig,
        private epcotConfig: EpcotConfig,
    ) {
        this.showDoneButton = this.epcotConfig.isEnabled;

        const messages = messageProvider.getMessages();

        this.messages = messages.betslip.settings;

        this.linearMessages = {
            settingsAcceptanceTitle: messages.linear.SettingsAcceptanceTitle,
            settingsNotificationsTitle: messages.linear.SettingsNotificationsTitle,
            settingsDone: messages.linear.SettingsDone,
        };

        const globalNotificationStatus = notificationService.globalNotificationStatus();
        this.notificationsViewModel = {
            [NotifyType.App]: {
                label: this.messages.appNotification,
                visible: globalNotificationStatus.isAppNotificationEnabled,
                enabled: true,
                value: false,
            },
            [NotifyType.Email]: {
                label: this.messages.emailNotification,
                visible: globalNotificationStatus.isEmailNotificationEnabled,
                enabled: true,
                value: false,
            },
        };

        this.recheckNotificationVisibility();

        this.picksChangeSub = this.store.select(betslipPicksListSelector).subscribe((picks) => {
            const notificationStatus = notificationService.notificationStatus(picks);
            this.notificationsViewModel = {
                [NotifyType.App]: {
                    ...this.notificationsViewModel[NotifyType.App],
                    enabled: notificationStatus.isAppNotificationEnabled,
                },
                [NotifyType.Email]: {
                    ...this.notificationsViewModel[NotifyType.Email],
                    enabled: notificationStatus.isEmailNotificationEnabled,
                },
            };
            changeDetector.markForCheck();
            if (this.deviceService.isMobile) {
                this.hideAppNotificationCheckBox = picks.some((x) => x.sportId === SportConstant.Horses || x.sportId === SportConstant.Greyhounds);
                this.recheckNotificationVisibility();
            }
        });
        this.settingsChangeSub = this.store.select(settingsSelector).subscribe((settings) => {
            this.selectedOddAcceptance = settings.oddsAcceptance;
            this.notificationsViewModel = {
                [NotifyType.App]: {
                    ...this.notificationsViewModel[NotifyType.App],
                    value: settings.notifications.appNotify,
                },
                [NotifyType.Email]: {
                    ...this.notificationsViewModel[NotifyType.Email],
                    value: settings.notifications.emailNotify,
                },
            };
            changeDetector.markForCheck();
        });
    }

    ngOnInit(): void {
        this.store.dispatch(LinearBetslipTrackingActions.loadSettings());
    }

    onOddsAcceptanceSelected(oddsAcceptance: OddsAcceptanceMode): void {
        this.selectedOddAcceptance = oddsAcceptance;
        setTimeout(() => {
            // In order to able to return back the default odd acceptance when there is an error saving settings
            // We need to wait browser rendering and then to fire the request action.
            this.store.dispatch(SettingsActions.requestSetOddAcceptance({ acceptance: oddsAcceptance }));
            this.store.dispatch(BetslipPlaceBetActions.validatePlaceButton({ isValid: false }));
        }, 1);
    }

    onNotificationToggled(isChecked: boolean, type: NotifyType): void {
        switch (type) {
            case NotifyType.App:
                this.store.dispatch(SettingsActions.requestSetAppNotify({ value: isChecked }));
                break;
            case NotifyType.Email:
                this.store.dispatch(SettingsActions.requestSetEmailNotify({ value: isChecked }));
                break;
        }
    }

    onSettingsClose() {
        this.store.dispatch(DigitalSettingsActions.closeSettings({ trackEvent: true }));
    }

    ngAfterViewInit(): void {
        this.settingsOpened.emit(this.settingsContent.nativeElement.offsetHeight);
    }

    ngOnDestroy(): void {
        this.settingsOpened.emit(0);
        this.picksChangeSub.unsubscribe();
        this.settingsChangeSub.unsubscribe();
    }

    private recheckNotificationVisibility(): void {
        const hasEmailNotifications = this.notificationsViewModel[NotifyType.Email].visible;
        const hasAppNotifications = this.notificationsViewModel[NotifyType.App].visible && !this.hideAppNotificationCheckBox;

        this.hideNotificationsSection = !hasEmailNotifications && !hasAppNotifications;
    }
}
